class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.addEventListener('click', (event) => {
      if (event.target.closest('#CartDrawer-Overlay')) this.close();
    });
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    requestAnimationFrame(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    requestAnimationFrame(() => {
      window.HomedockShipping?.restoreCartDrawer();
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);

document.addEventListener('click', async (event) => {
  const button = event.target.closest('[data-cart-discount-submit]');
  if (!button) return;

  event.preventDefault();
  event.stopPropagation();

  applyCartDrawerDiscount(button);
});

document.addEventListener('keydown', (event) => {
  const input = event.target.closest('[data-cart-discount-input]');
  if (!input || event.key !== 'Enter') return;

  event.preventDefault();
  event.stopPropagation();

  const coupon = input.closest('.cart-drawer__coupon');
  const button = coupon?.querySelector('[data-cart-discount-submit]');
  if (button) applyCartDrawerDiscount(button);
});

document.addEventListener('click', (event) => {
  const button = event.target.closest('[data-cart-discount-remove]');
  if (!button) return;

  event.preventDefault();
  event.stopPropagation();
  removeCartDrawerDiscount(button);
});

async function applyCartDrawerDiscount(button) {
  const coupon = button.closest('.cart-drawer__coupon');
  const input = coupon?.querySelector('[data-cart-discount-input]');
  const message = coupon?.querySelector('[data-cart-discount-message]');
  const code = input?.value.trim();

  if (!code) {
    setCartDrawerCouponMessage(message, 'Digite um cupom para aplicar.', 'error');
    input?.focus();
    return;
  }

  button.disabled = true;
  button.classList.add('is-loading');
  setCartDrawerCouponMessage(message, 'Aplicando cupom...');

  try {
    const hasAppliedCoupon = Boolean(document.querySelector('[data-cart-discount-remove]'));
    if (hasAppliedCoupon) {
      const clearResponse = await fetch(`${window.Shopify.routes.root}cart/update.js`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ discount: '' }),
      });

      if (!clearResponse.ok) {
        throw new Error('Não foi possível substituir o cupom atual.');
      }
    }

    const response = await fetch(`${window.Shopify.routes.root}cart/update.js`, {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ discount: code }),
    });

    const cart = await response.json().catch(() => null);
    if (!response.ok) {
      if (response.status === 422) {
        setCartDrawerCouponMessage(message, 'Cupom inválido.', 'error');
        return;
      }

      throw new Error('Não foi possível aplicar o cupom.');
    }

    const normalizedCode = code.toLocaleLowerCase('pt-BR');
    const appliedByCode = cart?.discount_codes?.some(
      (discount) =>
        String(discount.code || discount.title || '')
          .trim()
          .toLocaleLowerCase('pt-BR') === normalizedCode && discount.applicable !== false,
    );
    const appliedByApplication = cart?.cart_level_discount_applications?.some(
      (discount) =>
        discount.type === 'discount_code' &&
        String(discount.title || '').trim().toLocaleLowerCase('pt-BR') === normalizedCode,
    );

    await refreshCartDrawerAfterDiscount();

    const updatedInput = document.querySelector('#CartDrawer-Coupon');
    const updatedMessage = document.querySelector('[data-cart-discount-message]');
    const appliedInDrawer = Array.from(document.querySelectorAll('[data-cart-discount-remove]')).some(
      (discountButton) =>
        String(discountButton.dataset.discountCode || '')
          .trim()
          .toLocaleLowerCase('pt-BR') === normalizedCode,
    );
    const isApplied = appliedByCode || appliedByApplication || appliedInDrawer;

    if (updatedInput) updatedInput.value = code;
    setCartDrawerCouponMessage(
      updatedMessage,
      isApplied ? 'Cupom aplicado.' : 'Cupom inválido.',
      isApplied ? 'success' : 'error',
    );
  } catch (error) {
    console.error(error);
    setCartDrawerCouponMessage(message, 'Não foi possível aplicar o cupom.', 'error');
  } finally {
    button.disabled = false;
    button.classList.remove('is-loading');
  }
}

function setCartDrawerCouponMessage(message, text, status = '') {
  if (!message) return;

  message.textContent = text;
  message.classList.toggle('is-error', status === 'error');
  message.classList.toggle('is-success', status === 'success');

  if (status === 'error') {
    const coupon = message.closest('.cart-drawer__coupon');
    if (coupon) coupon.open = true;
  }
}

async function removeCartDrawerDiscount(button) {
  const drawer = button.closest('cart-drawer');
  const remainingCodes = Array.from(drawer?.querySelectorAll('[data-cart-discount-remove]') || [])
    .filter((discountButton) => discountButton !== button)
    .map((discountButton) => discountButton.dataset.discountCode)
    .filter(Boolean);

  button.disabled = true;
  button.setAttribute('aria-busy', 'true');

  try {
    const response = await fetch(`${window.Shopify.routes.root}cart/update.js`, {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ discount: remainingCodes.join(',') }),
    });

    if (!response.ok) throw new Error('Nao foi possivel remover o cupom.');

    await refreshCartDrawerAfterDiscount();
    const updatedMessage = document.querySelector('[data-cart-discount-message]');
    if (updatedMessage) updatedMessage.textContent = 'Cupom removido.';
  } catch (error) {
    console.error(error);
    button.disabled = false;
    button.removeAttribute('aria-busy');
  }
}

async function refreshCartDrawerAfterDiscount() {
  const response = await fetch(`${routes.cart_url}?section_id=cart-drawer`);
  const responseText = await response.text();
  const html = new DOMParser().parseFromString(responseText, 'text/html');
  const sourceDrawer = html.querySelector('#CartDrawer');
  const targetDrawer = document.querySelector('#CartDrawer');

  if (sourceDrawer && targetDrawer) {
    targetDrawer.innerHTML = sourceDrawer.innerHTML;
  }
}
